import React, { useState } from 'react';
import { Box, Typography, TextField, IconButton, Button, Dialog, DialogTitle, DialogContent, DialogActions, Card, CardContent, Tooltip, Chip } from '@mui/material';
import { Add, Edit, Delete, Close, Email, Phone, PersonAdd } from '@mui/icons-material';
import { DrivingQuestionsConfig } from './DrivingQuestionsConfig';
import MuiPhoneNumber from 'material-ui-phone-number';

export type AdditionalDriverSpec = { name: string; email: string; phone: string };

interface AdditionalDriversProps {
  config: DrivingQuestionsConfig;
  drivers: AdditionalDriverSpec[];
  onChange: (drivers: AdditionalDriverSpec[]) => void;
  mainDriverName?: string;
  mainDriverEmail?: string;
  fieldStyles?: any;
  onShowMessage: (message: string, variant: 'warning' | 'error' | 'success') => void;
}

const AdditionalDrivers: React.FC<AdditionalDriversProps> = ({ 
  config, 
  drivers, 
  onChange, 
  mainDriverName,
  mainDriverEmail,
  fieldStyles,
  onShowMessage 
}) => {
  const { maxAdditional } = config.driverLimits;
  const { additionalDrivers } = config;

  const [openModal, setOpenModal] = useState(false);
  const [modalMode, setModalMode] = useState<'add' | 'edit'>('add');
  const [editingDriverIndex, setEditingDriverIndex] = useState<number | null>(null);
  const [tempDriver, setTempDriver] = useState<AdditionalDriverSpec>({ name: '', email: '', phone: '' });
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [driverToDelete, setDriverToDelete] = useState<number | null>(null);

  const handleOpenAdd = () => {
    setModalMode('add');
    setEditingDriverIndex(null);
    setTempDriver({ name: '', email: '', phone: '' });
    setOpenModal(true);
  };

  const handleOpenEdit = (index: number) => {
    setModalMode('edit');
    setEditingDriverIndex(index);
    setTempDriver({ ...drivers[index] });
    setOpenModal(true);
  };

  const handleClose = () => {
    setOpenModal(false);
    setModalMode('add');
    setEditingDriverIndex(null);
    setTempDriver({ name: '', email: '', phone: '' });
  };

  const handleDeleteClick = (index: number) => {
    setDriverToDelete(index);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (driverToDelete !== null) {
      const updatedDrivers = drivers.filter((_, i) => i !== driverToDelete);
      onChange(updatedDrivers);
      setDeleteConfirmOpen(false);
      setDriverToDelete(null);
      onShowMessage('Driver removed successfully.', 'success');
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmOpen(false);
    setDriverToDelete(null);
  };

  const handleSave = () => {
    // Basic validation
    if (!tempDriver.name.trim()) {
      onShowMessage('Please enter a full name.', 'warning');
      return;
    }
    if (!tempDriver.email.trim()) {
      onShowMessage('Please enter an email.', 'warning');
      return;
    }

    const phoneDigits = tempDriver.phone?.replace(/\D/g, '') || '';
    if (!phoneDigits) {
      onShowMessage('Please enter a phone number.', 'warning');
      return;
    }

    const nameKey = tempDriver.name.trim().toLowerCase();
    const emailKey = tempDriver.email.trim().toLowerCase();

    // Check for duplicate emails against existing drivers (excluding current if editing)
    for (let i = 0; i < drivers.length; i++) {
      // Skip the driver being edited
      if (modalMode === 'edit' && i === editingDriverIndex) {
        continue;
      }

      const existingEmailKey = drivers[i].email.trim().toLowerCase();

      if (emailKey === existingEmailKey) {
        onShowMessage(`Duplicate email detected: ${tempDriver.email}. Each driver must use a unique email.`, 'warning');
        return;
      }
    }

    // Match email against main driver
    if (mainDriverEmail && emailKey === mainDriverEmail.trim().toLowerCase()) {
      onShowMessage(`Email matches the primary driver: ${tempDriver.email}.`, 'warning');
      return;
    }

    // Update drivers array
    let updatedDrivers: AdditionalDriverSpec[];
    if (modalMode === 'add') {
      // Check max limit
      if (drivers.length >= maxAdditional) {
        const message = additionalDrivers.maxReachedMessage.replace('{maxAdditional}', maxAdditional.toString());
        onShowMessage(message, 'warning');
        return;
      }
      updatedDrivers = [...drivers, tempDriver];
      onShowMessage('Driver added successfully.', 'success');
    } else {
      updatedDrivers = drivers.map((d, i) => i === editingDriverIndex ? tempDriver : d);
      onShowMessage('Driver updated successfully.', 'success');
    }

    onChange(updatedDrivers);
    handleClose();
  };

  const handleDriverChange = (field: keyof AdditionalDriverSpec, value: string) => {
    setTempDriver({ ...tempDriver, [field]: value });
  };

  return (
    <Box sx={{ mb: 3 }}>
      <Box sx={{ border: "1px solid #e0e0e0", borderRadius: 2, p: 2, bgcolor: 'background.paper' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body1" sx={{ color: '#333333', fontWeight: 600, m: 0 }}>
              Additional drivers
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              (Maximum of {maxAdditional})
            </Typography>
            {drivers.length > 0 && (
              <Chip 
                label={`${drivers.length} of ${maxAdditional}`} 
                size="small" 
                sx={{ 
                  bgcolor: drivers.length >= maxAdditional ? '#fef3cd' : '#e3f2fd',
                  color: drivers.length >= maxAdditional ? '#856404' : '#1976d2',
                  fontWeight: 600,
                  fontSize: '0.75rem',
                  height: 22
                }}
              />
            )}
          </Box>

          <Button
            variant="outlined"
            size="small"
            onClick={handleOpenAdd}
            startIcon={<Add sx={{ fontSize: 16 }} />}
            disabled={drivers.length >= maxAdditional}
            sx={{
              textTransform: 'uppercase',
              fontWeight: 600,
              fontSize: '0.7rem',
              px: 1,
              py: 0.3,
              minHeight: 'auto',
              height: 28,
              borderColor: '#1976d2',
              color: '#1976d2',
              '&:hover': {
                borderColor: '#1565c0',
                bgcolor: 'rgba(25, 118, 210, 0.04)',
              },
              '&:disabled': {
                borderColor: '#e0e0e0',
                color: '#9e9e9e',
              }
            }}
          >
            Add Driver
          </Button>
        </Box>
        
        {drivers.length === 0 && (
          <Box sx={{ 
            textAlign: 'center', 
            py: 3, 
            px: 2,
            bgcolor: '#f8f9fa',
            borderRadius: 2,
            border: '1px dashed #dee2e6'
          }}>
            <PersonAdd sx={{ fontSize: 48, color: '#adb5bd', mb: 1 }} />
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              No additional drivers added yet
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.5 }}>
              Click "Add Driver" to get started
            </Typography>
          </Box>
        )}
        
        {drivers.length > 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {drivers.map((d, i) => {
              return (
                <Card 
                  key={i} 
                  variant="outlined" 
                  sx={{ 
                    bgcolor: '#ffffff',
                    border: '1px solid #e0e0e0',
                    borderRadius: 2,
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                      borderColor: '#c0c0c0',
                    }
                  }}
                >
                  <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <Chip 
                            label={`Driver ${i + 1}`} 
                            size="small" 
                            sx={{ 
                              bgcolor: '#e3f2fd',
                              color: '#1976d2',
                              fontWeight: 600,
                              fontSize: '0.7rem',
                              height: 20
                            }}
                          />
                          <Typography 
                            variant="body1" 
                            sx={{ 
                              fontWeight: 600, 
                              color: '#333333',
                              fontSize: '1rem'
                            }}
                          >
                            {d.name}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Email sx={{ fontSize: 16, color: '#999' }} />
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                color: '#666666', 
                                fontSize: '0.875rem',
                                wordBreak: 'break-word'
                              }}
                            >
                              {d.email}
                            </Typography>
                          </Box>
                          {d.phone && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Phone sx={{ fontSize: 16, color: '#999' }} />
                              <Typography 
                                variant="body2" 
                                sx={{ 
                                  color: '#666666', 
                                  fontSize: '0.875rem'
                                }}
                              >
                                {d.phone}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                        <Tooltip title="Edit driver" arrow>
                          <IconButton
                            size="small"
                            onClick={() => handleOpenEdit(i)}
                            sx={{ 
                              color: '#1976d2',
                              '&:hover': {
                                bgcolor: 'rgba(25, 118, 210, 0.08)',
                              }
                            }}
                            aria-label={`Edit ${d.name}`}
                          >
                            <Edit fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Remove driver" arrow>
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteClick(i)}
                            sx={{ 
                              color: '#d32f2f',
                              '&:hover': {
                                bgcolor: 'rgba(211, 47, 47, 0.08)',
                              }
                            }}
                            aria-label={`Delete ${d.name}`}
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              );
            })}
          </Box>
        )}
      </Box>

      <Dialog open={openModal} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', color: '#333333', fontWeight: 700 }}>
          {modalMode === 'add' ? 'Add Driver' : 'Edit Driver'}
          <IconButton onClick={handleClose} size="small">
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            An upload link will be sent to the driver via email and SMS.
          </Typography>
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Full Name"
              value={tempDriver.name}
              onChange={(e) => handleDriverChange('name', e.target.value)}
              variant="standard"
              sx={fieldStyles}
              fullWidth
              required
            />
            <TextField
              label="Email"
              value={tempDriver.email}
              onChange={(e) => handleDriverChange('email', e.target.value)}
              variant="standard"
              sx={fieldStyles}
              fullWidth
              required
              type="email"
            />
            <MuiPhoneNumber
              label="Phone for SMS"
              value={tempDriver.phone}
              onChange={(value: any) => handleDriverChange('phone', value as string)}
              defaultCountry="us"
              variant="standard"
              sx={fieldStyles}
              fullWidth
              required
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleClose}>CANCEL</Button>
          <Button onClick={handleSave} variant="contained" disableElevation>
            {modalMode === 'add' ? 'ADD' : 'SAVE'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteConfirmOpen} onClose={handleDeleteCancel} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ color: '#333333', fontWeight: 700 }}>
          Remove Driver?
        </DialogTitle>
        <DialogContent>
          {driverToDelete !== null && drivers[driverToDelete] && (
            <Box>
              <Typography variant="body2" sx={{ mb: 2 }}>
                Are you sure you want to remove this driver?
              </Typography>
              <Box sx={{ 
                bgcolor: '#f8f9fa', 
                p: 2, 
                borderRadius: 1,
                border: '1px solid #dee2e6'
              }}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  {drivers[driverToDelete].name}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
                  {drivers[driverToDelete].email}
                </Typography>
                {drivers[driverToDelete].phone && (
                  <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
                    {drivers[driverToDelete].phone}
                  </Typography>
                )}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleDeleteCancel}>CANCEL</Button>
          <Button onClick={handleDeleteConfirm} variant="contained" color="error" disableElevation>
            DELETE
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdditionalDrivers;
